import { creatorbase, creatorbaseQueries } from '@round/api';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import uniq from 'lodash/uniq';
import {
    useBrandsQueriesByIds,
    useProjectsQueriesByIds,
    useTeamsQueriesByIds,
} from '@round/creatorbase-helpers/api-hooks';

const options = creatorbaseQueries.campaigns.options.infinite;
const infiniteListsKey = creatorbaseQueries.campaigns.keys.infiniteLists();

type Config = Partial<ReturnType<typeof options>>;

export type CampaignData = creatorbase.Campaign & {
    brand: creatorbase.Brand | null;
    project: creatorbase.Project | null;
    team: creatorbase.Team | null;
};

export default function useInfiniteCampaignsData(params: creatorbase.GetCampaignsParams, config: Config = {}) {
    const queryClient = useQueryClient();
    const query = useInfiniteQuery({
        ...options(params),
        ...config,
    });

    const projectIds = query.data?.pages.map((p) => uniq(p.results.map((c) => c.project_id))) ?? [];
    const projectsResults = useProjectsQueriesByIds(projectIds);

    const brandIds =
        query.data?.pages.map((_, pageIndex) =>
            uniq(projectsResults[pageIndex]?.data?.results.map((p) => p.brand_id) ?? [])
        ) ?? [];
    const brandsResults = useBrandsQueriesByIds(brandIds);

    const teamIds =
        query.data?.pages.map((_, pageIndex) =>
            uniq(projectsResults[pageIndex]?.data?.results.map((p) => p.team_id) ?? [])
        ) ?? [];
    const teamsResults = useTeamsQueriesByIds(teamIds);

    const data: CampaignData[] =
        query.data?.pages.flatMap((page, pageIndex) => {
            const projectsResult = projectsResults[pageIndex];
            const brandsResult = brandsResults[pageIndex];
            const teamsResult = teamsResults[pageIndex];

            return page.results.map((c) => {
                const project = projectsResult?.data?.results.find((p) => p.id === c.project_id) ?? null;
                const brand = brandsResult?.data?.results.find((b) => b.id === project?.brand_id) ?? null;
                const team = teamsResult?.data?.results.find((t) => t.id === project?.team_id) ?? null;
                return { ...c, project, brand, team };
            });
        }) ?? [];

    return {
        queries: {
            campaign: query,
            projects: projectsResults,
            brands: brandsResults,
            teams: teamsResults,
        },
        data,
        isRefetching: query.isRefetching,
        isFetching: query.isFetching,
        isPending: query.isPending,
        hasNextPage: query.hasNextPage,
        hasPreviousPage: query.hasPreviousPage,
        fetchNextPage: query.fetchNextPage,
        fetchPreviousPage: query.fetchPreviousPage,
        invalidate: () => queryClient.invalidateQueries({ queryKey: infiniteListsKey }),
        getIsBrandLoading: (campaign: CampaignData) => {
            const pageIndex = query.data?.pages.findIndex((page) => !!page.results.find((c) => c.id === campaign.id));
            if (typeof pageIndex !== 'number') {
                return false;
            }

            return brandsResults[pageIndex]?.isFetching || brandsResults[pageIndex]?.isPending;
        },
        getIsProjectLoading: (campaign: CampaignData) => {
            const pageIndex = query.data?.pages.findIndex((page) => !!page.results.find((c) => c.id === campaign.id));
            if (typeof pageIndex !== 'number') {
                return false;
            }

            return projectsResults[pageIndex]?.isFetching || projectsResults[pageIndex]?.isPending;
        },
    };
}
